/**
 * What the notification panel shows, resolved from the shopper's notifications for the design
 * system's views (`domain/notifications/`): the rows of one tab under Today and Earlier, and the
 * settings switches. The queries and mutations are `lib/notifications.ts`'s.
 */
import type { Notification } from "@loot-os/api-contract";
import {
  formatNotificationAge,
  groupNotificationsByDay,
} from "@loot-os/design-system/domain/notifications/notificationFormat";
import type {
  NotificationListContent,
  NotificationRowContent,
  NotificationSettingsContent,
} from "@loot-os/design-system/domain/notifications/notificationTypes";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import {
  type NotificationFilter,
  notificationSettingsQueryOptions,
  notificationsInfiniteQueryOptions,
  notificationTarget,
  useNow,
  useToggleNotificationCategory,
} from "../../lib/notifications.js";
import { useShopper } from "../../lib/shopper.js";

/**
 * One row, read on press: a storefront path routes in place and closes the panel, an https link
 * opens in a new tab, and a row with neither is a button that only marks it read.
 */
export function toNotificationRow(
  notification: Notification,
  now: Date,
  handlers: { onRead: (id: string) => void; onNavigate: () => void },
): NotificationRowContent {
  const read = () => {
    if (notification.readAt === null) handlers.onRead(notification.id);
  };
  const target = notificationTarget(notification);
  const url = notification.action?.url ?? "";
  return {
    id: notification.id,
    category: notification.category,
    icon: notification.icon,
    title: notification.title,
    body: notification.body,
    imageUrl: notification.imageUrl,
    actionLabel: notification.action?.label ?? null,
    createdAt: notification.createdAt,
    age: formatNotificationAge(notification.createdAt, now),
    unread: notification.readAt === null,
    target: target
      ? { href: target.kind === "path" ? url : target.href, external: target.kind === "external" }
      : null,
    onSelect:
      target?.kind === "path"
        ? () => {
            read();
            handlers.onNavigate();
          }
        : read,
  };
}

/** One tab's notifications, fetched as it shows and paged on scroll (or by its button). */
export function useNotificationListContent(
  filter: NotificationFilter,
  handlers: { onRead: (id: string) => void; onNavigate: () => void },
): NotificationListContent {
  const { client } = useShopper();
  const now = useNow();
  const query = useInfiniteQuery(notificationsInfiniteQueryOptions(client, filter));

  if (query.isPending) return { status: "loading", label: "Loading notifications" };
  if (query.isError) {
    return {
      status: "error",
      title: "Notifications did not load",
      body: "Check your connection and try again.",
      retry: { label: "Try again", onRetry: () => void query.refetch() },
    };
  }
  const notifications = query.data.pages.flatMap((page) => page.data);
  if (notifications.length === 0) {
    return filter === "unread"
      ? { status: "empty", title: "You're all caught up", body: "Nothing unread right now." }
      : {
          status: "empty",
          title: "No notifications yet",
          body: "Updates about your orders, balance and account show up here.",
        };
  }
  const { today, earlier } = groupNotificationsByDay(notifications, now);
  const rows = (list: Notification[]) => list.map((n) => toNotificationRow(n, now, handlers));
  return {
    status: "ready",
    groups: [
      { label: "Today", rows: rows(today) },
      { label: "Earlier", rows: rows(earlier) },
    ],
    more: query.hasNextPage
      ? {
          label: "Load more",
          loading: query.isFetchingNextPage,
          onLoadMore: () => void query.fetchNextPage(),
        }
      : null,
  };
}

/**
 * One switch per category the store sends, in the store's words. A required category stays on
 * and says so; any other saves the moment it is switched.
 */
export function useNotificationSettingsContent(enabled: boolean): NotificationSettingsContent {
  const { client } = useShopper();
  const query = useQuery({ ...notificationSettingsQueryOptions(client), enabled });
  const { toggle } = useToggleNotificationCategory();
  return {
    intro: "Choose what you hear about.",
    requiredLabel: "Always on",
    state: query.isPending
      ? { status: "loading", label: "Loading settings" }
      : query.isError
        ? { status: "error", message: "Your settings did not load. Close this and try again." }
        : {
            status: "ready",
            categories: query.data.categories.map((setting) => ({
              id: setting.category,
              label: setting.label,
              required: setting.required,
              enabled: setting.enabled,
              onChange: (on: boolean) => toggle(setting.category, on),
            })),
          },
  };
}
